import React from 'react'
import {
  getBrandFontByValue,
  getBrandFontOptions,
} from '../../branding/brandConfig'
import AccordionSection from './AccordionSection'

const LABEL_OVERFLOW_OPTIONS = [
  { id: 'horizontal', label: 'Horizontal' },
  { id: 'angled', label: 'Angled' },
  { id: 'stagger', label: 'Stagger' },
  { id: 'truncate', label: 'Truncate' },
]

const LABEL_SKIP_OPTIONS = [
  { value: 1, label: 'Every label' },
  { value: 2, label: 'Every 2nd' },
  { value: 3, label: 'Every 3rd' },
  { value: 5, label: 'Every 5th' },
]


function readLabelStrategy(rawViz) {
  try {
    const svgNode = rawViz?._node?.firstChild
    const raw = svgNode?.dataset?.lugsailLabelStrategy
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function buildAutoSummary(strategy) {
  if (!strategy) return null
  const parts = []
  const { x, y } = strategy

  if (x) {
    if (x.rotate) {
      parts.push(`X-axis rotated ${Math.abs(x.rotate)}°`)
    } else if (x.stagger) {
      parts.push('X-axis staggered')
    } else if (x.truncateTo) {
      parts.push(`X-axis shortened to ${x.truncateTo} chars`)
    }
  }

  if (y?.truncatedTo) {
    parts.push(`Y-axis shortened to ${y.truncatedTo} chars`)
  }

  if (!parts.length) return 'No adjustment needed · labels fit'
  return parts.join(' · ')
}

export default function TextStylePanel({
  theme,
  setTheme,
  supportsAxisLabelHandling = true,
  rawViz = null,
  children = null,
}) {
  const fontOptions = getBrandFontOptions()
  const selectedFont = getBrandFontByValue(theme.fontFamily) || fontOptions[0]
  const labelHandlingMode = theme.labelHandlingMode || 'auto'
  const labelOverflowStrategy = theme.labelOverflowStrategy || 'angled'
  const labelFontScale = Number(theme.labelFontScale ?? 1)
  const labelSizePercent = Math.round(labelFontScale * 100)
  const labelShowEveryNth = Number(theme.labelShowEveryNth ?? 1)
  const labelShowFullOnHover = theme.labelShowFullOnHover !== false
  const labelMaxCharacters = Number(theme.labelMaxCharacters ?? 12)
  const labelOutline = theme.labelOutline === true

  const set = (key, value) => setTheme({ ...theme, [key]: value })

  const labelStrategy = readLabelStrategy(rawViz)
  const autoSummary = labelHandlingMode === 'auto' ? buildAutoSummary(labelStrategy) : null

  return (
    <>
      <AccordionSection
        title="Text"
        subtitle="Font, titles, and chart label behavior."
        className="mb-3"
      >
        <div className="appearance-section appearance-section-framed assistant-accordion-panel">
          <div className="mb-2">
            <label className="mb-1 small d-block">Brand font</label>
            <select
              value={selectedFont?.value || ''}
              onChange={(event) => set('fontFamily', event.target.value)}
              className="custom-select raw-select"
            >
              {fontOptions.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <input
            value={theme.title}
            onChange={(event) => set('title', event.target.value)}
            placeholder="Chart title"
            className="form-control text-field mb-1"
          />
          <input
            value={theme.subtitle}
            onChange={(event) => set('subtitle', event.target.value)}
            placeholder="Subtitle / source note"
            className="form-control text-field mb-2"
          />
          <div className="small text-muted">
            Titles, subtitles, source notes, and chart text are configured here.
          </div>
        </div>
      </AccordionSection>
      <AccordionSection
        title="Label handling"
        subtitle={
          supportsAxisLabelHandling
            ? 'Size, outline, and overflow behavior for chart labels.'
            : 'Size and readability controls for chart labels.'
        }
        className="text-control-accordion mb-3"
      >
        <div className="text-control-outline-row">
          <div>
            <div className="text-control-row__label">Text outline</div>
            <div className="text-control-row__sub">
              Add a high-contrast outline behind chart labels for readability.
            </div>
          </div>
          <button
            type="button"
            className={[
              'text-control-toggle',
              labelOutline ? 'text-control-toggle-active' : '',
            ].join(' ')}
            onClick={() => set('labelOutline', !labelOutline)}
            aria-pressed={labelOutline}
          >
            <span />
          </button>
        </div>

        <div className="text-control-card assistant-accordion-panel">
          <div className="text-control-card__header">
            {supportsAxisLabelHandling ? (
              <div className="text-control-card__header-top">
                <button
                  type="button"
                  className={[
                    'text-control-card__auto-pill',
                    labelHandlingMode === 'auto'
                      ? 'text-control-card__auto-pill-active'
                      : '',
                  ].join(' ')}
                  onClick={() =>
                    set(
                      'labelHandlingMode',
                      labelHandlingMode === 'auto' ? 'manual' : 'auto'
                    )
                  }
                >
                  Auto
                </button>
              </div>
            ) : null}
            <div className="text-control-card__title-group">
              <div className="text-control-card__title">
                {supportsAxisLabelHandling ? 'Axis & category labels' : 'Chart labels'}
              </div>
            </div>
          </div>

          {supportsAxisLabelHandling && labelHandlingMode === 'auto' ? (
            <div className="text-control-card__auto-state">
              <div className="text-control-card__auto-icon" aria-hidden="true">
                ✦
              </div>
              <div className="text-control-card__auto-title">Smart label handling</div>
              <div className="text-control-card__auto-copy">
                Labels stay readable by rotating or truncating when space gets tight.
              </div>
              {autoSummary && (
                <div className="text-control-card__auto-summary">
                  {autoSummary}
                </div>
              )}
            </div>
          ) : (
            <div className="text-control-card__body">
              <div className="text-control-row">
                <div>
                  <div className="text-control-row__label">Label size</div>
                  <div className="text-control-row__sub">
                    Scale chart labels without changing the dataset layout.
                  </div>
                </div>
                <div className="text-control-slider-row">
                  <input
                    type="range"
                    min="80"
                    max="300"
                    step="5"
                    value={labelSizePercent}
                    onChange={(event) =>
                      set(
                        'labelFontScale',
                        Number((Number(event.target.value) / 100).toFixed(2))
                      )
                    }
                  />
                  <span>{labelSizePercent}%</span>
                </div>
              </div>
              <div className="text-control-row text-control-row-stack">
                <div>
                  <div className="text-control-row__label">Overflow handling</div>
                  <div className="text-control-row__sub">
                    {supportsAxisLabelHandling
                      ? 'Choose what happens when labels collide.'
                      : 'Overflow strategies apply to axis-style charts only.'}
                  </div>
                </div>
                <div
                  className="text-control-segmented"
                  role="tablist"
                  aria-label="Label overflow handling"
                >
                  {LABEL_OVERFLOW_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={[
                        'text-control-segmented__option',
                        !supportsAxisLabelHandling
                          ? 'text-control-segmented__option-disabled'
                          : '',
                        labelOverflowStrategy === option.id
                          ? 'text-control-segmented__option-active'
                          : '',
                      ].join(' ')}
                      onClick={() =>
                        supportsAxisLabelHandling &&
                        set('labelOverflowStrategy', option.id)
                      }
                      disabled={!supportsAxisLabelHandling}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {supportsAxisLabelHandling && labelOverflowStrategy === 'truncate' ? (
                <div className="text-control-row">
                  <div>
                    <div className="text-control-row__label">Max characters</div>
                    <div className="text-control-row__sub">
                      Labels cut to this length with an ellipsis.
                    </div>
                  </div>
                  <div className="text-control-slider-row">
                    <input
                      type="range"
                      min="6"
                      max="24"
                      step="1"
                      value={labelMaxCharacters}
                      onChange={(event) =>
                        set('labelMaxCharacters', Number(event.target.value))
                      }
                    />
                    <span>{labelMaxCharacters}</span>
                  </div>
                </div>
              ) : null}

              <div className="text-control-row">
                <div>
                  <div className="text-control-row__label">Show every Nth label</div>
                  <div className="text-control-row__sub">
                    {supportsAxisLabelHandling
                      ? 'Skip labels when density is too high.'
                      : 'Only available on charts with axis tick labels.'}
                  </div>
                </div>
                <select
                  value={labelShowEveryNth}
                  onChange={(event) =>
                    set('labelShowEveryNth', Number(event.target.value))
                  }
                  className="custom-select raw-select text-control-select"
                  disabled={!supportsAxisLabelHandling}
                >
                  {LABEL_SKIP_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-control-row">
                <div>
                  <div className="text-control-row__label">Show full label on hover</div>
                  <div className="text-control-row__sub">
                    {supportsAxisLabelHandling
                      ? 'Add tooltips for truncated or crowded labels.'
                      : 'Keep full labels available where the chart supports hover titles.'}
                  </div>
                </div>
                <button
                  type="button"
                  className={[
                    'text-control-toggle',
                    labelShowFullOnHover ? 'text-control-toggle-active' : '',
                  ].join(' ')}
                  onClick={() => set('labelShowFullOnHover', !labelShowFullOnHover)}
                  aria-pressed={labelShowFullOnHover}
                >
                  <span />
                </button>
              </div>
            </div>
          )}
        </div>
        {children ? <div className="mt-3">{children}</div> : null}
      </AccordionSection>
    </>
  )
}
